<template>
  <div class="calendar-container">
    <div class="calendar-month-control">
      <div class="prev icon">
        <button type="button" class="btn btn-action" @click.stop="onPrevMonth(selectedDate)">
          <i class="fas fa-angle-left"></i>
        </button>
      </div>
      <div class="month">{{getCalendarTitle(selectedDate)}}</div>
      <div class="next icon">
        <button type="button" class="btn btn-action" @click.stop="onNextMonth(selectedDate)">
          <i class="fas fa-angle-right"></i>
        </button>
      </div>
    </div>
    <div class="calendar-header-container">
      <div class="week-name calendar-item" v-for="(name, dayIndex) in daysOfWeek" :key="dayIndex">
        {{name}}
      </div>
    </div>
    <div class="calendar-item-container">
      <router-link
        v-for="(day, itemIndex) in dayList"
        :to="generateScheduleDateLink(day)"
        :key="itemIndex"
        :class="getCalendarItemClass(getDate(day), itemIndex)"
        class="calendar-item">
        {{getDate(day)}} <i class="fas fa-circle icon" :class="generateScheduleIcon(day)"></i>
      </router-link>
    </div>
  </div>
</template>

<script>
import { getAvailableScheduleByMonth } from '@/api/TimeShift';
import _ from 'lodash';
import {
  addDays,
  addMonths,
  differenceInDays,
  endOfMonth,
  format,
  formatISO,
  getDate,
  getHours,
  getMonth,
  getYear,
  isSameMonth,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
  parseISO,
} from 'date-fns';
import { mapState } from 'vuex';

export default {
  data() {
    return {
      scheduleMonthly: [],
      dayList: [],
      today: null,
      selectedDate: null,
      defaultStartTimeHour: 12,
      defaultEndTimeHour: 14,
      minStartTimeNextDateHour: 0,
      maxStartTimeNextDateHour: 4,
      realFirstDayOfMonthIndex: 0,
    };
  },
  computed: {
    ...mapState({
      channels: state => state.teamInformation.channels,
    }),
    hasChannel() {
      return this.channels && this.channels.length > 0;
    },
    scheduleYear() {
      return _.get(this.$route.params, 'scheduleYear', null);
    },
    scheduleMonth() {
      return _.get(this.$route.params, 'scheduleMonth', null);
    },
    scheduleDay() {
      return _.get(this.$route.params, 'scheduleDay', null);
    },
    daysOfWeek() {
      return [
        this.$t('common:sunday_shortest'),
        this.$t('common:monday_shortest'),
        this.$t('common:tuesday_shortest'),
        this.$t('common:wednesday_shortest'),
        this.$t('common:thursday_shortest'),
        this.$t('common:friday_shortest'),
        this.$t('common:saturday_shortest'),
      ];
    },
    teamName() {
      return this.$route.params.teamName;
    },
  },
  watch: {
    hasChannel() {
      if (this.hasChannel) {
        this.loadScheduleAvailability();
      }
    },
  },
  created() {
    this.today = new Date();
    if (this.scheduleYear && this.scheduleMonth && this.scheduleDay) {
      const month = Number(this.scheduleMonth) - 1;
      this.load(formatISO(startOfDay(new Date(this.scheduleYear, month, this.scheduleDay))));
    } else {
      this.load(formatISO(startOfDay(new Date())));
    }
  },
  methods: {
    getDate,
    async loadScheduleAvailability() {
      this.scheduleMonthly = [];
      if (!this.hasChannel) {
        return;
      }
      const id = this.channels[0]._id;
      let date = this.selectedDate;
      if (typeof date === 'string') {
        date = parseISO(date);
      }
      const yearMonth = format(date, 'yyyy-MM');
      const response = await getAvailableScheduleByMonth(id, yearMonth);
      this.scheduleMonthly = _.get(response, 'data.monthlySchedules', null);
    },
    getCalendarItemClass(day, slot) {
      let className;
      let parsedDate = this.selectedDate;
      if (typeof parsedDate === 'string') {
        parsedDate = parseISO(this.selectedDate);
      }
      if (isSameMonth(this.today, parsedDate)) {
        if (day === getDate(this.today)) {
          className = 'today ';
        }
      }
      if (slot >= 0 && slot <= 6 && day.toString().length === 2) {
        // First Week Conditions
        className = 'other-month';
      }
      if (slot >= 25 && day.toString().length === 1) {
        // Last Week Condition
        className = 'other-month';
      }
      return className;
    },
    getCalendarTitle(selectDate) {
      const date = new Date(selectDate);
      const month = getMonth(date);
      const year = getYear(date);
      let monthText;
      switch (month) {
        case 0:
          monthText = this.$t('common:january');
          break;
        case 1:
          monthText = this.$t('common:february');
          break;
        case 2:
          monthText = this.$t('common:march');
          break;
        case 3:
          monthText = this.$t('common:april');
          break;
        case 4:
          monthText = this.$t('common:may');
          break;
        case 5:
          monthText = this.$t('common:june');
          break;
        case 6:
          monthText = this.$t('common:july');
          break;
        case 7:
          monthText = this.$t('common:august');
          break;
        case 8:
          monthText = this.$t('common:september');
          break;
        case 9:
          monthText = this.$t('common:october');
          break;
        case 10:
          monthText = this.$t('common:november');
          break;
        default:
          monthText = this.$t('common:december');
      }
      return `${monthText} ${year}`;
    },
    load(selectedDate) {
      this.selectedDate = _.cloneDeep(selectedDate);
      this.dayList = this.generateDayOfCalendarMonth(this.selectedDate);
      this.loadScheduleAvailability();
    },
    onNextMonth(selectedDate) {
      this.selectedDate = this.getNextMonth(selectedDate);
      this.dayList = this.generateDayOfCalendarMonth(this.selectedDate);
      this.loadScheduleAvailability();
    },
    onPrevMonth(selectedDate) {
      this.selectedDate = this.getPrevMonth(selectedDate);
      this.dayList = this.generateDayOfCalendarMonth(this.selectedDate);
      this.loadScheduleAvailability();
    },
    getNextMonth(date) {
      return addMonths(new Date(date), 1);
    },
    getPrevMonth(date) {
      return subMonths(new Date(date), 1);
    },
    generateScheduleIcon(dateTime) {
      const date = getDate(dateTime);
      const month = dateTime.getMonth() + 1;
      let dateString;
      let monthString;
      if (date < 10) {
        dateString = `0${date}`;
      } else {
        dateString = date.toString();
      }
      if (month < 10) {
        monthString = `0${month}`;
      } else {
        monthString = month.toString();
      }
      const checker = `${monthString}-${dateString}`;
      const isCreated = _.find(this.scheduleMonthly,
        schedule => (schedule.scheduleDate.slice(-5) === checker) && schedule.availabled);
      if (_.isEmpty(this.scheduleMonthly)) {
        return '';
      }
      if (!_.isEmpty(this.scheduleMonthly) && isCreated) {
        return 'available';
      }
      return 'unavailable';
    },
    generateScheduleDateLink(date) {
      const scheduleDate = this.formatScheduleDate(date);
      return `/${this.teamName}/timeshift/${scheduleDate}`;
    },
    formatScheduleDate(selectDate) {
      return format(new Date(selectDate), 'yyyy/MM/dd');
    },
    getStartDayOfCalendar(startDayOfMonth) {
      const amountdaysOfWeek = 7;
      const daysOfWeek = startDayOfMonth.getDay();
      if (daysOfWeek < amountdaysOfWeek) {
        return subDays(new Date(startDayOfMonth), daysOfWeek + 1);
      }

      return startDayOfMonth;
    },
    getAmountOfSlot(startDayOfCalendar, endDayOfCalendar) {
      const diff = differenceInDays(endDayOfCalendar, startDayOfCalendar);
      const amountdaysOfWeek = 7;
      const slotOfWeek = Math.ceil((diff / amountdaysOfWeek));
      return slotOfWeek * amountdaysOfWeek;
    },
    generateDayOfCalendarMonth(selectMonth) {
      const startDayOfMonth = startOfMonth(new Date(selectMonth));
      const startDayOfCalendar = this.getStartDayOfCalendar(startDayOfMonth);
      const endDayOfCalendar = endOfMonth(new Date(selectMonth));
      const amountOfSlot = this.getAmountOfSlot(startDayOfCalendar, endDayOfCalendar);
      const dayList = [];

      for (let i = 0; i < amountOfSlot; i += 1) {
        dayList.push(addDays(new Date(startDayOfCalendar), i + 1));
      }

      return dayList;
    },
    isInPeriod(time, startHour, endHour) {
      return time >= startHour && time < endHour;
    },
    mappingScheduleTime(scheduleDate, startTime, endTime) {
      const tomorrow = new Date(scheduleDate);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const startHour = getHours(startTime);
      const isNewDayTime = this.isInPeriod(startHour, this.minStartTimeNextDateHour, this.maxStartTimeNextDateHour);

      // Before Midnight -> After Midnight
      if (isNewDayTime && (startTime < tomorrow)) {
        return {
          startTime: addDays(startTime, 1),
          endTime: addDays(endTime, 1),
        };
      }
      // After Midnight -> Before Midnight
      if (!isNewDayTime && (startTime > tomorrow)) {
        return {
          startTime: addDays(startTime, -1),
          endTime: addDays(endTime, -1),
        };
      }

      return {
        startTime: new Date(startTime),
        endTime: new Date(endTime),
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/variables.scss';

.calendar-container {
  background-color: var(--md-theme-default-background, #151618);
  border-radius: 2px;

  .calendar-month-control {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 26px 20px 0;
    .month {
      font-size: $font-size-base * 1.25;
    }
  }

  .calendar-header-container {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: -12px;
    padding: 16px 10px 0;
    .week-name {
      height: 32px;
      font-size: $font-size-base * 0.875;
      color: #FFF;
    }
    .calendar-item {
      &:hover {
        background: transparent;
      }
    }
  }

  .calendar-item-container {
    display: flex;
    flex-wrap: wrap;
    padding: 10px 10px 30px;
  }

  .calendar-item {
    flex: 0 0 calc(100% / 7);
    height: 42px;
    margin: 4px 0;
    padding-bottom: 10px;
    border-radius: 4px;
    color: #d7d7d7;

    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    .icon {
      position: absolute;
      bottom: 6px;
      font-size: $font-size-sm * 0.5;
      color: $gray-300;
      transition: color 300ms ease;
      &.available {
        color: #1abbff;
      }
      &.unavailable {
        color: #a90d0d;
      }
    }

    &:hover {
      background: #161f30;
      color: #FFF;
      text-decoration: none;
    }

    &.other-month {
      color: $gray-600;
      .icon {
        color: $gray-600;
      }
    }

    &.today {
      background-color: #161f30;
    }

    &.router-link-exact-active {
      background: $btn-primary;
      color: #FFF;
    }
  }

}

@media screen and (max-width: 1280px) {
  .calendar-container {
    .calendar-item {
      height: 36px;
    }
  }
}
</style>
